import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  AvatarGroup,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SettingsIcon from '@mui/icons-material/Settings';

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'highlightcolor',
})<{
  highlightcolor?: string;
}>(({ theme, highlightcolor }) => {
  // Only allow known palette keys
  const allowedKeys = [
    'primary', 'secondary', 'info', 'success', 'warning', 'error', 'grey', 'text', 'divider', 'background', 'common'
  ] as const;
  type PaletteKey = typeof allowedKeys[number];
  let resolvedColor = theme.palette.divider;
  if (highlightcolor && highlightcolor.includes('.')) {
    const [colorKey, shade] = highlightcolor.split('.');
    if (allowedKeys.includes(colorKey as PaletteKey) && shade in (theme.palette as any)[colorKey]) {
      resolvedColor = (theme.palette as any)[colorKey][shade];
    } else {
      resolvedColor = highlightcolor;
    }
  } else if (highlightcolor) {
    resolvedColor = highlightcolor;
  }
  return {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'visible',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s',
  cursor: 'pointer',
    border: `2.5px solid ${resolvedColor}`,
    boxShadow: theme.shadows[2],
    '&:hover, &:focus-within': {
      transform: 'translateY(-4px) scale(1.01)',
    boxShadow: theme.shadows[8],
      borderColor: resolvedColor,
      outline: `2.5px solid ${resolvedColor}`,
      outlineOffset: '2px',
    },
    // High contrast focus ring for accessibility
    '&:focus-visible': {
      outline: `3px solid ${theme.palette.info.main}`,
      outlineOffset: '2px',
  },
  };
});

const FarmAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  border: `4px solid ${theme.palette.background.paper}`,
  marginTop: -40,
  backgroundColor: theme.palette.primary.main,
}));

const StatsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const StatItem = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  textAlign: 'center',
}));

interface FarmCardProps {
  farm: {
    id: string;
    name: string;
    location: string;
    imageUrl?: string;
  };
  onEdit: (farmId: string) => void;
  onManageWorkers: (farmId: string) => void;
  onSettings: (farmId: string) => void;
  /**
   * Optional color for card highlight (e.g., 'primary.main', 'info.main')
   * Used for tutorial vs real data distinction
   */
  color?: string;
  /**
   * Stats for the farm (tasks, workers, acres)
   */
  stats: {
    tasks: number;
    workers: number;
    acres: number;
  };
}

const FarmCard: React.FC<FarmCardProps> = ({
  farm,
  onEdit,
  onManageWorkers,
  onSettings,
  color,
  stats,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  /**
   * Opens the farm card action menu
   * @param event - The mouse event that triggered the menu opening
   */
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  /**
   * Closes the farm card action menu
   */
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  /**
   * Navigates to the farm details page when the card is clicked
   */
  const handleCardClick = () => {
    navigate(`/dashboard/my-farms/${farm.id}`);
  };

  /**
   * Creates an event handler for farm actions from the menu
   * @param action - The action function to execute with the farm ID
   * @returns An event handler function that prevents event propagation and executes the action
   */
  const handleAction = (action: (farmId: string) => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    handleMenuClose();
    action(farm.id);
  };

  return (
    <StyledCard
      onClick={handleCardClick}
      tabIndex={0} // Make card focusable for keyboard users
      highlightcolor={color}
      aria-label={`View details for ${farm.name}`}
    >
      <Box
        sx={{
          height: 100,
          background: (theme) => {
            const allowedKeys = [
              'primary', 'secondary', 'info', 'success', 'warning', 'error', 'grey', 'text', 'divider', 'background', 'common'
            ] as const;
            type PaletteKey = typeof allowedKeys[number];
            let resolvedColor = theme.palette.primary.main;
            if (color && color.includes('.')) {
              const [colorKey, shade] = color.split('.');
              if (allowedKeys.includes(colorKey as PaletteKey) && shade in (theme.palette as any)[colorKey]) {
                resolvedColor = (theme.palette as any)[colorKey][shade];
              } else {
                resolvedColor = color;
              }
            } else if (color) {
              resolvedColor = color;
            }
            return `linear-gradient(45deg, ${resolvedColor}, ${theme.palette.primary.dark})`;
          },
          borderRadius: '8px 8px 0 0',
        }}
      />
      
      <CardContent sx={{ pt: 0, position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <FarmAvatar src={farm.imageUrl} alt={farm.name}>
            {!farm.imageUrl && farm.name.charAt(0)}
          </FarmAvatar>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Typography variant="h5" gutterBottom>
          {farm.name}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {farm.location}
        </Typography>

        <StatsBox>
          <StatItem>
            <Typography variant="h6">{stats.tasks}</Typography>
            <Typography variant="body2" color="text.secondary">Tasks</Typography>
          </StatItem>
          <StatItem>
            <Typography variant="h6">{stats.workers}</Typography>
            <Typography variant="body2" color="text.secondary">Workers</Typography>
          </StatItem>
          <StatItem>
            <Typography variant="h6">{stats.acres}</Typography>
            <Typography variant="body2" color="text.secondary">Acres</Typography>
          </StatItem>
        </StatsBox>

        {/* Worker avatars removed for simplicity and robustness. Add here if needed in the future. */}
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleAction(onEdit)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" /> Edit Farm
        </MenuItem>
        <MenuItem onClick={handleAction(onManageWorkers)}>
          <GroupAddIcon sx={{ mr: 1 }} fontSize="small" /> Manage Workers
        </MenuItem>
        <MenuItem onClick={handleAction(onSettings)}>
          <SettingsIcon sx={{ mr: 1 }} fontSize="small" /> Settings
        </MenuItem>
      </Menu>
    </StyledCard>
  );
};

export default FarmCard;